/** @file eventWorldPoint: the world point of an event: entity + node position + the body-space offset. */
import type { Vec3 } from '@zilo/pose3d-math/vec3/vec3';
import type { PoseBuffer, PoseParams } from '@zilo/pose3d-pose/contract';
import { puppetTransform } from '@zilo/pose3d-pose/chain/puppetTransform';
import { rotateZScaled } from '@zilo/pose3d-pose/chain/rotateZScaled';
import { POSE_LAYOUT as L } from '@zilo/pose3d-pose/layout/poseLayout';
import type { Rig } from '@zilo/pose3d-pose/rig/rigTypes';

/**
 * Decision 21: `entityPos + pose position of node + rotateZScaled(T, offset, zScaleRotate)` with `T` the puppet transform
 * (`offset` is in body space, mirrored first with `mirrorX`); an absent or unknown `node` reports the first node (the root).
 */
export function eventWorldPoint(pose: PoseBuffer, rig: Rig, node: string | undefined, offset: Vec3 | undefined, params: PoseParams, entityPos: Vec3): Vec3 {
  const o = (node === undefined ? 0 : (rig.index.get(node) ?? 0)) * L.stride;
  const local: Vec3 | undefined = offset === undefined || params.mirrorX !== true ? offset : [-offset[0], offset[1], offset[2]];
  const d = local === undefined ? [0, 0, 0] as const : rotateZScaled(puppetTransform(params), local, params.profile.zScaleRotate ?? 1);
  return [
    entityPos[0] + (pose[o + L.px] ?? 0) + d[0],
    entityPos[1] + (pose[o + L.py] ?? 0) + d[1],
    entityPos[2] + (pose[o + L.pz] ?? 0) + d[2],
  ];
}
